import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQueryWithErrorHandling } from './baseQuery';

// ── Types ────────────────────────────────────────────────────────────────────

export type TreatmentCheckInV2Status =
  | 'DRAFT'
  | 'IN_CALL'
  | 'NOTES_TAKEN'
  | 'GENERATING'
  | 'IN_REVIEW'
  | 'FINALIZED'
  | 'NO_ANSWER'
  | 'LEFT_VOICEMAIL'
  | 'LEFT_TEXT';

export type CallOutcome =
  | 'CONNECTED'
  | 'NO_ANSWER'
  | 'LEFT_VOICEMAIL'
  | 'LEFT_TEXT';

export type ReviewDecisionType =
  | 'accepted'
  | 'edited'
  | 'rejected'
  | 'added'
  | 'placeholder_created'
  | 'dismissed';

export type ObjectiveStatus =
  | 'open'
  | 'done'
  | 'not_done'
  | 'skipped'
  | 'dismissed'
  | 'carried_forward';

export type ObjectiveKind =
  | 'document'
  | 'follow_up'
  | 'treatment_gap'
  | 'diagnostic_follow_up'
  | 'provider_follow_up'
  | 'mobrain_gap'
  | 'operator_added';

export type ObjectivePriorityBand = 'now' | 'soon' | 'later';

export type ObjectiveAssessmentState = 'covered' | 'not_covered' | 'unclear';
export type TextOutreachWorkflowState = 'drafted' | 'copied' | 'marked_sent';
export type TextOutreachDraftSource = 'llm' | 'fallback';

export interface ObjectiveSourceBundleItem {
  source_type: string;
  source_id: string | null;
  reason: string;
}

export interface ObjectiveTaskContext {
  task_id: string | null;
  task_type: string | null;
  due_at: string | null;
  task_note_summary: string | null;
}

export interface ObjectiveProviderContext {
  provider_id: string | null;
  provider_name: string | null;
  specialty: string | null;
}

export interface ObjectiveAssessment {
  assessment: ObjectiveAssessmentState | null;
  confidence: number | null;
  detail: string | null;
  evidence_excerpt: string | null;
  suggested_status: 'done' | 'leave_open' | null;
  agrees_with_operator: boolean | null;
  recommended_carry_forward: boolean;
  suggested_next_call_label: string | null;
}

export interface ObjectiveOperatorState {
  status: ObjectiveStatus | null;
  updated_by: string | null;
  updated_at: string | null;
}

export interface CheckInObjective {
  id: string;
  label: string;
  short_label: string;
  status: ObjectiveStatus;
  kind: ObjectiveKind;
  priority_band: ObjectivePriorityBand;
  pinned: boolean;
  rank: number;
  client_relevant: boolean;
  freshness: string;
  primary_source: string;
  source_bundle: ObjectiveSourceBundleItem[];
  source_ids: string[];
  task_context: ObjectiveTaskContext | null;
  provider_context: ObjectiveProviderContext | null;
  why_now: string;
  done_when: string | null;
  why_this_matters: string;
  operator_state: ObjectiveOperatorState | null;
  ai_state: ObjectiveAssessment | null;
}

export interface CarryForwardSuggestion {
  objective_id: string;
  label: string;
  selected: boolean;
  source: string;
}

export interface ObjectiveState {
  version: number;
  resolved_at: string | null;
  revision: number;
  locked_for_call: boolean;
  source_origin: 'precomputed' | 'fallback';
  source_generated_at: string | null;
  source_revision: number | null;
  visible_objective_ids: string[];
  objectives: CheckInObjective[];
  manual_objectives: string[];
  carry_forward_suggestions: CarryForwardSuggestion[];
  resolver_summary: {
    task_candidates: number;
    prior_call_candidates: number;
    mobrain_candidates: number;
    heuristic_candidates: number;
    suppressed_stale_candidates: number;
  };
}

export interface TextOutreach {
  workflow_state: TextOutreachWorkflowState;
  suggested_text: string;
  final_text: string;
  draft_basis_summary: string;
  draft_source: TextOutreachDraftSource;
  was_edited: boolean;
  copy_count: number;
  generated_at: string | null;
  copied_at: string | null;
  marked_sent_at: string | null;
  generated_by_id: string | null;
  marked_sent_by_id: string | null;
  model: string;
  prompt_version: string;
  firm_sop_key: string;
}

export interface TreatmentTextThreadMessage {
  id: string;
  content: string;
  is_inbound: boolean;
  source_timestamp: string | null;
  from_number: string | null;
  to_number: string | null;
  source_system: string;
}

export interface TreatmentTextThreadContext {
  client_name: string;
  recipient_phone: string | null;
  sender_phone: string | null;
  sender_configured: boolean;
  messages: TreatmentTextThreadMessage[];
}

export interface TreatmentCheckInV2 {
  id: string;
  matter_id: string;
  created_by_id: string;
  status: TreatmentCheckInV2Status;
  ringcentral_call_id: string | null;
  audio_conversation_id: string | null;
  operator_notes: string;
  triggered_by_task_id: string | null;

  // Welcome/routine
  is_welcome_call: boolean;

  // Channel
  channel: string;

  // Call outcome
  call_outcome: CallOutcome | null;
  call_outcome_notes: string;

  // AI extraction
  extraction_result: Record<string, unknown> | null;
  extraction_generation_id: string | null;
  extraction_model: string;
  extraction_prompt_version: string;
  generation_count: number;
  last_generated_at: string | null;

  // Operator review decisions
  accepted_changes: { items: Record<string, ReviewDecisionPayload> } | null;

  objective_state: ObjectiveState | null;

  // Pre-computed call context (providers, objectives, last check-in summary)
  call_priorities: {
    providers: Array<{
      provider_id: string;
      name: string;
      specialty: string;
      status: string;
      frequency: string;
    }>;
    objectives: string[];
    last_check_in: {
      date: string;
      summary: string;
      provider_notes?: Array<{
        provider_name: string;
        notes: string;
        status: string;
      }>;
    } | null;
  } | null;

  // Self-directed objectives for next call
  next_call_objectives: string[] | null;

  // Manual treatment text beta
  text_outreach: TextOutreach | null;

  // Finalization
  finalized_at: string | null;
  finalized_by_id: string | null;

  created_at: string;
  modified_at: string;
}

export interface CheckInTranscriptSegment {
  id: string;
  text: string;
  speaker: number | null;
  start_time: number;
  end_time: number;
}

// ── Request/Response Types ──────────────────────────────────────────────────

export interface CallOutcomeRequest {
  matterId: string;
  checkInId: string;
  outcome: CallOutcome;
  notes?: string;
}

export interface EndCallResponse {
  success: boolean;
  state: 'ended' | 'already_ended';
  message: string | null;
}

export interface GenerateEnhancedNotesRequest {
  matterId: string;
  checkInId: string;
}

export interface SaveReviewDecisionRequest {
  matterId: string;
  checkInId: string;
  review_key: string;
  decision: ReviewDecisionType;
  generation_id: string;
  approved_payload?: Record<string, unknown> | null;
  resolution?: Record<string, unknown> | null;
}

export interface FinalizeCheckInV2Request {
  matterId: string;
  checkInId: string;
}

export interface ObjectiveUpdatePayload {
  objective_id: string;
  status?: ObjectiveStatus;
  carry_forward_selected?: boolean;
}

export interface CopyTextDraftRequest {
  matterId: string;
  checkInId: string;
  finalText: string;
}

export interface ReviewDecisionPayload {
  review_key: string;
  decision: ReviewDecisionType;
  generation_id: string;
  reviewed_at: string;
  approved_payload: Record<string, unknown> | null;
  resolution: Record<string, unknown> | null;
}

export interface FinalizeConflictResponse {
  error: string;
  stale_review_keys: string[];
}

// ── API Slice ────────────────────────────────────────────────────────────────

export const treatmentCheckInV2ApiClient = createApi({
  reducerPath: 'treatmentCheckInV2Api',
  baseQuery: baseQueryWithErrorHandling,
  tagTypes: ['TreatmentCheckInsV2', 'TreatmentCheckInV2'] as const,
  endpoints: (builder) => ({
    checkV2Access: builder.query<{ allowed: boolean }, { matterId: string }>({
      query: ({ matterId }) =>
        `api/matters/${matterId}/treatment-check-ins-v2/access/`,
    }),

    // List
    listCheckInsV2: builder.query<TreatmentCheckInV2[], string>({
      query: (matterId) => `api/matters/${matterId}/treatment-check-ins-v2/`,
      providesTags: (result, error, matterId) => [
        { type: 'TreatmentCheckInsV2', id: matterId },
      ],
    }),

    // Retrieve
    getCheckInV2: builder.query<
      TreatmentCheckInV2,
      { matterId: string; checkInId: string }
    >({
      query: ({ matterId, checkInId }) => ({
        url: `api/matters/${matterId}/treatment-check-ins-v2/${checkInId}/`,
        method: 'GET',
      }),
      providesTags: (result, error, { matterId, checkInId }) => [
        { type: 'TreatmentCheckInV2', id: `${matterId}-${checkInId}` },
      ],
    }),

    // Create
    createCheckInV2: builder.mutation<
      TreatmentCheckInV2,
      { matterId: string; triggeredByTaskId?: string | null; channel?: string }
    >({
      query: ({ matterId, triggeredByTaskId, channel }) => ({
        url: `api/matters/${matterId}/treatment-check-ins-v2/`,
        method: 'POST',
        body: {
          ...(triggeredByTaskId && {
            triggered_by_task_id: triggeredByTaskId,
          }),
          ...(channel && { channel }),
        },
      }),
      invalidatesTags: (result, error, { matterId }) => [
        { type: 'TreatmentCheckInsV2', id: matterId },
      ],
    }),

    // Update operator notes — no cache invalidation to avoid resetting
    // the TipTap editor cursor position during auto-save.
    updateOperatorNotes: builder.mutation<
      TreatmentCheckInV2,
      { matterId: string; checkInId: string; operatorNotes: string }
    >({
      query: ({ matterId, checkInId, operatorNotes }) => ({
        url: `api/matters/${matterId}/treatment-check-ins-v2/${checkInId}/`,
        method: 'PATCH',
        body: { operator_notes: operatorNotes },
      }),
    }),

    // Record call outcome
    recordCallOutcome: builder.mutation<TreatmentCheckInV2, CallOutcomeRequest>(
      {
        query: ({ matterId, checkInId, outcome, notes }) => ({
          url: `api/matters/${matterId}/treatment-check-ins-v2/${checkInId}/call-outcome/`,
          method: 'POST',
          body: { outcome, notes },
        }),
        invalidatesTags: (result, error, { matterId, checkInId }) => [
          { type: 'TreatmentCheckInsV2', id: matterId },
          { type: 'TreatmentCheckInV2', id: `${matterId}-${checkInId}` },
        ],
      },
    ),

    endCheckInCall: builder.mutation<
      EndCallResponse,
      { matterId: string; checkInId: string }
    >({
      query: ({ matterId, checkInId }) => ({
        url: `api/matters/${matterId}/treatment-check-ins-v2/${checkInId}/end-call/`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, { matterId, checkInId }) => [
        { type: 'TreatmentCheckInsV2', id: matterId },
        { type: 'TreatmentCheckInV2', id: `${matterId}-${checkInId}` },
      ],
    }),

    // Generate AI-enhanced notes (stub — returns 501 until B-22)
    generateEnhancedNotes: builder.mutation<
      TreatmentCheckInV2,
      GenerateEnhancedNotesRequest
    >({
      query: ({ matterId, checkInId }) => ({
        url: `api/matters/${matterId}/treatment-check-ins-v2/${checkInId}/generate-notes/`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, { matterId, checkInId }) => [
        { type: 'TreatmentCheckInsV2', id: matterId },
        { type: 'TreatmentCheckInV2', id: `${matterId}-${checkInId}` },
      ],
    }),

    // Save a single review decision
    saveReviewDecision: builder.mutation<
      TreatmentCheckInV2,
      SaveReviewDecisionRequest
    >({
      query: ({
        matterId,
        checkInId,
        review_key,
        decision,
        generation_id,
        approved_payload,
        resolution,
      }) => ({
        url: `api/matters/${matterId}/treatment-check-ins-v2/${checkInId}/review/`,
        method: 'PATCH',
        body: {
          review_key,
          decision,
          generation_id,
          approved_payload,
          resolution,
        },
      }),
      invalidatesTags: (result, error, { matterId, checkInId }) => [
        { type: 'TreatmentCheckInsV2', id: matterId },
        { type: 'TreatmentCheckInV2', id: `${matterId}-${checkInId}` },
      ],
    }),

    updateObjectives: builder.mutation<
      TreatmentCheckInV2,
      {
        matterId: string;
        checkInId: string;
        revision: number;
        updates: ObjectiveUpdatePayload[];
      }
    >({
      query: ({ matterId, checkInId, revision, updates }) => ({
        url: `api/matters/${matterId}/treatment-check-ins-v2/${checkInId}/objectives/`,
        method: 'PATCH',
        body: { revision, updates },
      }),
      invalidatesTags: (result, error, { matterId, checkInId }) => [
        { type: 'TreatmentCheckInV2', id: `${matterId}-${checkInId}` },
      ],
    }),

    // Transcript segments for a check-in
    getCheckInTranscriptSegments: builder.query<
      CheckInTranscriptSegment[],
      { matterId: string; checkInId: string }
    >({
      query: ({ matterId, checkInId }) =>
        `api/matters/${matterId}/treatment-check-ins-v2/${checkInId}/transcript-segments/`,
    }),

    getTextThreadContext: builder.query<
      TreatmentTextThreadContext,
      { matterId: string; checkInId: string }
    >({
      query: ({ matterId, checkInId }) =>
        `api/matters/${matterId}/treatment-check-ins-v2/${checkInId}/text-thread/`,
    }),

    generateTextDraft: builder.mutation<
      TreatmentCheckInV2,
      { matterId: string; checkInId: string }
    >({
      query: ({ matterId, checkInId }) => ({
        url: `api/matters/${matterId}/treatment-check-ins-v2/${checkInId}/text-draft/`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, { matterId, checkInId }) => [
        { type: 'TreatmentCheckInV2', id: `${matterId}-${checkInId}` },
      ],
    }),

    copyTextDraft: builder.mutation<TreatmentCheckInV2, CopyTextDraftRequest>({
      query: ({ matterId, checkInId, finalText }) => ({
        url: `api/matters/${matterId}/treatment-check-ins-v2/${checkInId}/text-copy/`,
        method: 'POST',
        body: { final_text: finalText },
      }),
      invalidatesTags: (result, error, { matterId, checkInId }) => [
        { type: 'TreatmentCheckInV2', id: `${matterId}-${checkInId}` },
      ],
    }),

    markTextSent: builder.mutation<
      TreatmentCheckInV2,
      { matterId: string; checkInId: string }
    >({
      query: ({ matterId, checkInId }) => ({
        url: `api/matters/${matterId}/treatment-check-ins-v2/${checkInId}/text-mark-sent/`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, { matterId, checkInId }) => [
        { type: 'TreatmentCheckInV2', id: `${matterId}-${checkInId}` },
      ],
    }),

    // Save next-call objectives
    setNextCallObjectives: builder.mutation<
      TreatmentCheckInV2,
      { matterId: string; checkInId: string; objectives: string[] }
    >({
      query: ({ matterId, checkInId, objectives }) => ({
        url: `api/matters/${matterId}/treatment-check-ins-v2/${checkInId}/next-objectives/`,
        method: 'POST',
        body: { objectives },
      }),
      invalidatesTags: (result, error, { matterId, checkInId }) => [
        { type: 'TreatmentCheckInV2', id: `${matterId}-${checkInId}` },
      ],
    }),

    // Delete check-in
    deleteCheckInV2: builder.mutation<
      void,
      { matterId: string; checkInId: string }
    >({
      query: ({ matterId, checkInId }) => ({
        url: `api/matters/${matterId}/treatment-check-ins-v2/${checkInId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { matterId }) => [
        { type: 'TreatmentCheckInsV2', id: matterId },
      ],
    }),

    // Finalize check-in
    finalizeCheckInV2: builder.mutation<
      TreatmentCheckInV2,
      FinalizeCheckInV2Request
    >({
      query: ({ matterId, checkInId }) => ({
        url: `api/matters/${matterId}/treatment-check-ins-v2/${checkInId}/finalize/`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, { matterId, checkInId }) => [
        { type: 'TreatmentCheckInsV2', id: matterId },
        { type: 'TreatmentCheckInV2', id: `${matterId}-${checkInId}` },
      ],
    }),
  }),
});

// ── Exported Hooks ───────────────────────────────────────────────────────────

export const {
  useCheckV2AccessQuery,
  useListCheckInsV2Query,
  useGetCheckInV2Query,
  useCreateCheckInV2Mutation,
  useUpdateOperatorNotesMutation,
  useUpdateObjectivesMutation,
  useRecordCallOutcomeMutation,
  useEndCheckInCallMutation,
  useGenerateEnhancedNotesMutation,
  useSaveReviewDecisionMutation,
  useSetNextCallObjectivesMutation,
  useDeleteCheckInV2Mutation,
  useFinalizeCheckInV2Mutation,
  useGetCheckInTranscriptSegmentsQuery,
  useGetTextThreadContextQuery,
  useGenerateTextDraftMutation,
  useCopyTextDraftMutation,
  useMarkTextSentMutation,
} = treatmentCheckInV2ApiClient;
